import bassSvg from "./clefs/bass.svg";
import trebleSvg from "./clefs/treble.svg";

// Vector clefs painted onto the staff. The glyphs are SVG assets (esbuild inlines
// them as data-URIs) loaded as Images, so they render without a musical-symbol font
// installed on the host. Each is rastered once per size and tinted to the staff hue
// via `source-in` compositing (the source SVGs are dark) — no SVG editing.

type ClefKind = "treble" | "bass";

// width/height of each source glyph — sizes the clef before the Image has loaded.
const ASPECT: Record<ClefKind, number> = { treble: 15.186 / 40.768, bass: 18 / 20 };
const SOURCES: Record<ClefKind, string> = { treble: trebleSvg, bass: bassSvg };

const images: Partial<Record<ClefKind, HTMLImageElement>> = {};
function imageFor(kind: ClefKind): HTMLImageElement {
  const cached = images[kind];
  if (cached) return cached;
  const img = new Image();
  img.src = SOURCES[kind];
  images[kind] = img;
  return img;
}

// Tinted rasters keyed by kind+size+colour. Sizes change only on resize/split, so
// this holds a handful of entries.
const tintCache = new Map<string, HTMLCanvasElement>();
function tinted(kind: ClefKind, w: number, h: number, color: string): HTMLCanvasElement | null {
  const img = imageFor(kind);
  if (!img.complete || img.naturalWidth === 0 || w <= 0 || h <= 0) return null;
  const key = `${kind}:${w}x${h}:${color}`;
  const hit = tintCache.get(key);
  if (hit) return hit;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const cx = c.getContext("2d");
  if (!cx) return null;
  cx.drawImage(img, 0, 0, w, h);
  cx.globalCompositeOperation = "source-in";
  cx.fillStyle = color;
  cx.fillRect(0, 0, w, h);
  tintCache.set(key, c);
  return c;
}

/**
 * Draw a clef in `color`, height `h`, centered vertically on `middleY`, with its right
 * edge at `rightX`. Returns the drawn width (still returns the width if the glyph image
 * hasn't loaded yet — it paints on a later frame once `img.complete`).
 */
export function drawClef(
  ctx: CanvasRenderingContext2D,
  kind: ClefKind,
  rightX: number,
  middleY: number,
  h: number,
  color: string,
): number {
  const w = Math.round(h * ASPECT[kind]);
  const raster = tinted(kind, w, Math.round(h), color);
  if (raster) ctx.drawImage(raster, rightX - w, middleY - h / 2, w, h);
  return w;
}
